// Accounting.jsx
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext.jsx';
import { formatCurrency } from '../utils/formatters';
import { FaChartLine, FaMoneyBillWave, FaArrowUp, FaArrowDown, FaBalanceScale, FaReceipt, FaFileInvoiceDollar, FaWallet, FaPercentage } from 'react-icons/fa';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const Accounting = () => {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext); // ✅ FIXED: removed setUser, added logout
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // VAT Report modal state
  const [showVatModal, setShowVatModal]   = useState(false);
  const [vatMonth, setVatMonth]           = useState('2026-01');
  const [vatFormat, setVatFormat]         = useState('xlsx');
  const [vatGenerating, setVatGenerating] = useState(false);

  // ✅ ADDED: Helper to get correct auth token
  const getAuthToken = () => {
    return localStorage.getItem('adminAuthToken') || localStorage.getItem('authToken');
  };

  // Financial Data States
  const [financialData, setFinancialData] = useState({
    totalRevenue: 0,
    totalExpenses: 0,
    netProfit: 0,
    grossProfit: 0,
    operatingExpenses: 0,
    cogs: 0,
    cashBalance: 0,
    vatPayable: 0,
    accountsReceivable: 0,
    accountsPayable: 0,
    revenueBySubBusiness: { Salon: 0, Gym: 0, Spa: 0 },
    expensesBySubBusiness: { Salon: 0, Gym: 0, Spa: 0 },
    profitBySubBusiness: { Salon: 0, Gym: 0, Spa: 0 },
  });

  const [transactions, setTransactions] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [invoices, setInvoices] = useState([]);
  
  // Set default to today's date
  const [dateRange, setDateRange] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  });

  // Expense Form State
  const [expenseForm, setExpenseForm] = useState({
    amount: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    subBusiness: 'Salon',
    category: 'operating',
    paymentMethod: 'cash',
  });

  // Invoice Form State
  const [invoiceForm, setInvoiceForm] = useState({
    customerName: '',
    customerDetails: '',
    date: new Date().toISOString().split('T')[0],
    subBusiness: 'Salon',
    items: [{ description: '', unitPrice: '', quantity: '' }],
    subtotal: '',
    vat: '',
    totalAmount: '',
  });

  useEffect(() => {
    const fetchAllData = async () => {
      // ✅ FIXED: Use helper function
      const token = getAuthToken();
      if (!token || !user) {
        setError('No authentication token or user found. Please log in.');
        // ✅ FIXED: Clear admin tokens and use logout from context
        localStorage.removeItem('adminAuthToken');
        localStorage.removeItem('adminUser');
        localStorage.removeItem('authToken'); // legacy cleanup
        localStorage.removeItem('user'); // legacy cleanup
        logout(); // ✅ FIXED: Use logout from AuthContext instead of setUser(null)
        navigate('/login', { state: { message: 'Please log in to access this page' } });
        return;
      }

      // Set axios default header
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      setLoading(true);
      try {
        // Fetch data from the correct endpoints
        const [summaryRes, transactionsRes, expensesRes, invoicesRes] = await Promise.all([
          axios.get(`${import.meta.env.VITE_API_URL}/api/accounting/summary`, {
            params: { startDate: dateRange.startDate, endDate: dateRange.endDate },
          }),
          axios.get(`${import.meta.env.VITE_API_URL}/api/pos-transactions`, {
            params: { startDate: dateRange.startDate, endDate: dateRange.endDate },
          }),
          axios.get(`${import.meta.env.VITE_API_URL}/api/expenses`, {
            params: { startDate: dateRange.startDate, endDate: dateRange.endDate },
          }),
          axios.get(`${import.meta.env.VITE_API_URL}/api/invoices`, {
            params: { startDate: dateRange.startDate, endDate: dateRange.endDate },
          }),
        ]);

        // Process the summary data from accounting endpoint
        if (summaryRes.data) {
          setFinancialData(summaryRes.data);
        }

        // Handle transactions data - it's returned as an array
        setTransactions(Array.isArray(transactionsRes.data) ? transactionsRes.data : []);
        setExpenses(Array.isArray(expensesRes.data) ? expensesRes.data : []);
        setInvoices(Array.isArray(invoicesRes.data) ? invoicesRes.data : []);
        
        setError('');
      } catch (error) {
        console.error('Error fetching data:', error.response?.data || error.message);
        if (error.response?.status === 401) {
          setError('Session expired. Please log in again.');
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          logout(); // ✅ FIXED
          navigate('/login', { state: { message: 'Session expired. Please log in again.' } });
        } else {
          setError(`Failed to fetch data: ${error.response?.data?.message || error.message}`);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [dateRange, navigate, user, logout]); // ✅ FIXED: replaced setUser with logout

  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    const token = getAuthToken(); // ✅ FIXED
    if (!token || !user) {
      setError('No authentication token or user found. Please log in.');
      logout(); // ✅ FIXED
      navigate('/login', { state: { message: 'Please log in to access this page' } });
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      await axios.post(`${import.meta.env.VITE_API_URL}/api/expenses`, expenseForm);

      setSuccess('Expense recorded successfully');
      setExpenseForm({
        amount: '',
        description: '',
        date: new Date().toISOString().split('T')[0],
        subBusiness: 'Salon',
        category: 'operating',
        paymentMethod: 'cash',
      });

      // Refresh expenses
      const expensesRes = await axios.get(`${import.meta.env.VITE_API_URL}/api/expenses`, {
        params: { startDate: dateRange.startDate, endDate: dateRange.endDate },
      });
      setExpenses(Array.isArray(expensesRes.data) ? expensesRes.data : []);
    } catch (error) {
      console.error('Error recording expense:', error.response?.data || error.message);
      if (error.response?.status === 401) {
        setError('Session expired. Please log in again.');
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        logout(); // ✅ FIXED
        navigate('/login', { state: { message: 'Session expired. Please log in again.' } });
      } else {
        setError(`Failed to record expense: ${error.response?.data?.message || error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInvoiceSubmit = async (e) => {
    e.preventDefault();
    const token = getAuthToken(); // ✅ FIXED
    if (!token || !user) {
      setError('No authentication token or user found. Please log in.');
      logout(); // ✅ FIXED
      navigate('/login', { state: { message: 'Please log in to access this page' } });
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      await axios.post(`${import.meta.env.VITE_API_URL}/api/invoices`, invoiceForm);

      setSuccess('Invoice created successfully');
      setInvoiceForm({
        customerName: '',
        customerDetails: '',
        date: new Date().toISOString().split('T')[0],
        subBusiness: 'Salon',
        items: [{ description: '', unitPrice: '', quantity: '' }],
        subtotal: '',
        vat: '',
        totalAmount: '',
      });

      // Refresh invoices
      const invoicesRes = await axios.get(`${import.meta.env.VITE_API_URL}/api/invoices`, {
        params: { startDate: dateRange.startDate, endDate: dateRange.endDate },
      });
      setInvoices(Array.isArray(invoicesRes.data) ? invoicesRes.data : []);
    } catch (error) {
      console.error('Error creating invoice:', error.response?.data || error.message);
      if (error.response?.status === 401) {
        setError('Session expired. Please log in again.');
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        logout(); // ✅ FIXED
        navigate('/login', { state: { message: 'Session expired. Please log in again.' } });
      } else {
        setError(`Failed to create invoice: ${error.response?.data?.message || error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const addInvoiceItem = () => {
    setInvoiceForm((prev) => ({
      ...prev,
      items: [...prev.items, { description: '', unitPrice: '', quantity: '' }],
    }));
  };

  const updateInvoiceItem = (index, field, value) => {
    setInvoiceForm((prev) => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };
      const subtotal = newItems.reduce(
        (sum, item) => sum + (Number(item.unitPrice) || 0) * (Number(item.quantity) || 0),
        0
      );
      const vat = subtotal * 0.075;
      const totalAmount = subtotal + vat;
      return { ...prev, items: newItems, subtotal, vat, totalAmount };
    });
  };

  const exportReport = async (type) => {
    const token = getAuthToken(); // ✅ FIXED
    if (!token || !user) {
      setError('No authentication token or user found. Please log in.');
      logout(); // ✅ FIXED
      navigate('/login', { state: { message: 'Please log in to access this page' } });
      return;
    }

    try {
      const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/reports/export/${type}`, {
        params: { startDate: dateRange.startDate, endDate: dateRange.endDate },
        responseType: 'blob',
      });

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}-report-${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error(`Error exporting ${type} report:`, error.response?.data || error.message);
      if (error.response?.status === 401) {
        setError('Session expired. Please log in again.');
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        logout(); // ✅ FIXED
        navigate('/login', { state: { message: 'Session expired. Please log in again.' } });
      } else {
        setError(`Failed to export ${type} report: ${error.response?.data?.message || error.message}`);
      }
    }
  };

  // Build month options from Jan 2026 up to current month
  const vatMonthOptions = (() => {
    const options = [];
    const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    const now = new Date();
    let y = 2026, m = 0; // start Jan 2026
    while (y < now.getFullYear() || (y === now.getFullYear() && m <= now.getMonth())) {
      const val = `${y}-${String(m + 1).padStart(2, '0')}`;
      options.push({ value: val, label: `${monthNames[m]} ${y}` });
      m++;
      if (m > 11) { m = 0; y++; }
    }
    return options;
  })();

  const generateVatReport = async () => {
    setVatGenerating(true);
    setError('');
    try {
      if (vatFormat === 'xlsx') {
        // Backend generates fully formatted ExcelJS file
        const response = await axios.get(
          `${import.meta.env.VITE_API_URL}/api/accounting/vat-report`,
          { params: { month: vatMonth }, responseType: 'blob' }
        );
        const url = window.URL.createObjectURL(response.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Ellas_VAT_Report_${vatMonth}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        // PDF: fetch transactions then generate client-side with jsPDF
        const [year, mon] = vatMonth.split('-');
        const startDate = `${year}-${mon}-01`;
        const lastDay   = new Date(+year, +mon, 0).getDate();
        const endDate   = `${year}-${mon}-${String(lastDay).padStart(2, '0')}`;

        const res  = await axios.get(`${import.meta.env.VITE_API_URL}/api/pos-transactions`, { params: { startDate, endDate } });
        const txns = Array.isArray(res.data) ? res.data : [];

        const monthLabel = vatMonthOptions.find(o => o.value === vatMonth)?.label || vatMonth;
        const fmt        = (n) => parseFloat((n || 0).toFixed(2));
        const fDate      = (d) => new Date(d).toLocaleDateString('en-GB');
        const fTime      = (d) => new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
        const fCur       = (n) => `N${fmt(n).toLocaleString('en-NG', { minimumFractionDigits: 2 })}`;

        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

        // ── Page 1: Sales Transactions ───────────────────────────────────────
        doc.setFillColor(74, 26, 107);
        doc.rect(0, 0, 297, 22, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text(`ELLAS PLACE  —  ${monthLabel.toUpperCase()}  SALES REPORT`, 148.5, 14, { align: 'center' });

        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text(`Generated: ${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}   |   VAT Rate: 7.5%`, 148.5, 28, { align: 'center' });

        let totSub = 0, totVat = 0, totAmt = 0;
        const salesBody = txns.map((tx, i) => {
          const itemStr = (tx.items || []).map(it => `${it.name} x${it.quantity}`).join(', ');
          totSub += fmt(tx.subtotal); totVat += fmt(tx.vat); totAmt += fmt(tx.totalAmount);
          return [
            i + 1,
            fDate(tx.createdAt),
            fTime(tx.createdAt),
            tx.customer?.name || '—',
            tx.customer?.phone || '—',
            itemStr,
            tx.category || '—',
            Array.isArray(tx.subBusiness) ? tx.subBusiness.join(', ') : (tx.subBusiness || '—'),
            (tx.paymentMethod || '').replace(/_/g, ' ').toUpperCase(),
            fCur(tx.subtotal),
            fCur(tx.vat),
            fCur(tx.totalAmount),
            (tx.status || '').toUpperCase(),
          ];
        });
        salesBody.push(['', 'TOTALS', '', '', '', '', '', '', '', fCur(totSub), fCur(totVat), fCur(totAmt), `${txns.length} txns`]);

        autoTable(doc, {
          startY: 32,
          head: [['#','Date','Time','Customer','Phone','Items','Category','Sub-Biz','Payment','Subtotal','VAT','Total','Status']],
          body: salesBody,
          styles: { fontSize: 7, cellPadding: 2, overflow: 'linebreak' },
          headStyles: { fillColor: [74, 26, 107], textColor: 255, fontStyle: 'bold', halign: 'center' },
          alternateRowStyles: { fillColor: [243, 235, 249] },
          columnStyles: {
            0: { cellWidth: 7 },  1: { cellWidth: 18 }, 2: { cellWidth: 14 },
            3: { cellWidth: 24 }, 4: { cellWidth: 20 }, 5: { cellWidth: 40 },
            6: { cellWidth: 18 }, 7: { cellWidth: 16 }, 8: { cellWidth: 18 },
            9: { cellWidth: 20, halign: 'right' }, 10: { cellWidth: 16, halign: 'right' },
            11: { cellWidth: 22, halign: 'right' }, 12: { cellWidth: 16 },
          },
          didParseCell: (data) => {
            if (data.row.index === salesBody.length - 1) {
              data.cell.styles.fillColor = [109, 40, 217];
              data.cell.styles.textColor = [255, 255, 255];
              data.cell.styles.fontStyle = 'bold';
            }
          },
          margin: { left: 5, right: 5 },
        });

        // ── Page 2: Daily VAT Summary ─────────────────────────────────────────
        doc.addPage();
        doc.setFillColor(185, 28, 28);
        doc.rect(0, 0, 297, 22, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text(`ELLAS PLACE  —  ${monthLabel.toUpperCase()}  VAT COLLECTION REPORT`, 148.5, 14, { align: 'center' });

        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text(`VAT Rate: 7.5%   |   Generated: ${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}`, 148.5, 28, { align: 'center' });

        const byDate = {};
        txns.forEach(tx => {
          const d = fDate(tx.createdAt);
          if (!byDate[d]) byDate[d] = { count: 0, subtotal: 0, vat: 0, total: 0 };
          byDate[d].count++;
          byDate[d].subtotal += fmt(tx.subtotal);
          byDate[d].vat      += fmt(tx.vat);
          byDate[d].total    += fmt(tx.totalAmount);
        });
        const dates = Object.keys(byDate).sort((a, b) => {
          const ts = s => { const [d, m, y] = s.split('/'); return new Date(`${y}-${m}-${d}`).getTime(); };
          return ts(a) - ts(b);
        });

        let cumVat = 0, totS = 0, totV = 0, totR = 0;
        const vatBody = dates.map(d => {
          const rec = byDate[d];
          cumVat += rec.vat; totS += rec.subtotal; totV += rec.vat; totR += rec.total;
          return [d, rec.count, fCur(rec.subtotal), fCur(rec.vat), fCur(rec.total), fCur(cumVat)];
        });
        vatBody.push(['MONTHLY TOTAL', txns.length, fCur(totS), fCur(totV), fCur(totR), fCur(totV)]);

        autoTable(doc, {
          startY: 32,
          head: [['Date','No. of Transactions','Total Sales (NGN)','VAT Collected (NGN)','Total Revenue (NGN)','Cumulative VAT (NGN)']],
          body: vatBody,
          styles: { fontSize: 9, cellPadding: 3 },
          headStyles: { fillColor: [185, 28, 28], textColor: 255, fontStyle: 'bold', halign: 'center' },
          alternateRowStyles: { fillColor: [254, 226, 226] },
          columnStyles: {
            0: { cellWidth: 30 }, 1: { cellWidth: 35, halign: 'center' },
            2: { cellWidth: 40, halign: 'right' }, 3: { cellWidth: 40, halign: 'right' },
            4: { cellWidth: 40, halign: 'right' }, 5: { cellWidth: 40, halign: 'right' },
          },
          didParseCell: (data) => {
            if (data.row.index === vatBody.length - 1) {
              data.cell.styles.fillColor = [220, 38, 38];
              data.cell.styles.textColor = [255, 255, 255];
              data.cell.styles.fontStyle = 'bold';
            }
          },
          margin: { left: 20, right: 20 },
        });

        doc.save(`Ellas_VAT_Report_${vatMonth}.pdf`);
      }

      setShowVatModal(false);
    } catch (err) {
      console.error('VAT report error:', err);
      setError('Failed to generate VAT report: ' + (err.response?.data?.message || err.message));
    } finally {
      setVatGenerating(false);
    }
  };

  const handleDateRangeChange = (field, value) => {
    setDateRange((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Quick date selection functions
const setToday = () => {
  const today = new Date();
  const formatLocalDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  setDateRange({ 
    startDate: formatLocalDate(today), 
    endDate: formatLocalDate(today) 
  });
};

  const setThisWeek = () => {
  const today = new Date();
  const currentDay = today.getDay();
  
  const firstDay = new Date(today);
  firstDay.setDate(today.getDate() - currentDay);
  
  const lastDay = new Date(today);
  lastDay.setDate(today.getDate() + (6 - currentDay));
  
  const formatLocalDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  
  setDateRange({
    startDate: formatLocalDate(firstDay),
    endDate: formatLocalDate(lastDay),
  });
};

  const setThisMonth = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();
  
  // ✅ Use local date string, not ISO (which converts to UTC)
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  
  const formatLocalDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  
  setDateRange({
    startDate: formatLocalDate(firstDay),
    endDate: formatLocalDate(lastDay),
  });
};

  // ✅ Helper function to expand multi-sub-business transactions into separate rows
  const expandTransactions = (transactions) => {
    const expanded = [];

    transactions.forEach(transaction => {
      const subBusinessArray = Array.isArray(transaction.subBusiness)
        ? transaction.subBusiness
        : [transaction.subBusiness];

      // If single sub-business, add transaction as-is
      if (subBusinessArray.length === 1) {
        expanded.push({
          ...transaction,
          displaySubBusiness: subBusinessArray[0] || 'N/A'
        });
        return;
      }

      // If multiple sub-businesses, split into separate rows
      const itemsBySubBusiness = {};

      // Group items by sub-business and calculate total items value
      let totalItemsValue = 0;
      (transaction.items || []).forEach(item => {
        const itemSubBusiness = item.subBusiness || 'Unknown';
        const itemValue = (item.price || 0) * (item.quantity || 1);

        if (!itemsBySubBusiness[itemSubBusiness]) {
          itemsBySubBusiness[itemSubBusiness] = [];
        }
        itemsBySubBusiness[itemSubBusiness].push(item);
        totalItemsValue += itemValue;
      });

      // Create a row for each sub-business
      Object.entries(itemsBySubBusiness).forEach(([subBusiness, items]) => {
        // Calculate sub-total for this sub-business
        const subTotal = items.reduce((sum, item) => {
          return sum + ((item.price || 0) * (item.quantity || 1));
        }, 0);

        // Calculate proportional VAT and discount based on item values (not total amount)
        const proportion = totalItemsValue > 0 ? subTotal / totalItemsValue : 0;
        const proportionalVAT = (transaction.vat || 0) * proportion;
        const proportionalDiscount = (transaction.discount || 0) * proportion;

        expanded.push({
          ...transaction,
          _id: `${transaction._id}-${subBusiness}`, // Unique ID for React key
          items: items,
          displaySubBusiness: subBusiness,
          subtotal: subTotal,
          proportionalVAT: proportionalVAT,
          totalAmount: subTotal + proportionalVAT - proportionalDiscount
        });
      });
    });

    return expanded;
  };

  const calculateProfitMargin = () => {
    if (financialData.totalRevenue === 0) return 0;
    return ((financialData.netProfit / financialData.totalRevenue) * 100).toFixed(2);
  };

  const getFinancialHealthStatus = () => {
    const margin = parseFloat(calculateProfitMargin());
    if (margin > 20) return { status: 'Excellent', color: 'text-green-600' };
    if (margin > 10) return { status: 'Good', color: 'text-blue-600' };
    if (margin > 0) return { status: 'Fair', color: 'text-yellow-600' };
    return { status: 'Poor', color: 'text-red-600' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-pink-50 p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="min-h-screen bg-pink-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Accounting & Finance</h1>
          <p className="text-gray-600">Ella's Place Salon, Spa & Ladies Gym - Financial Management</p>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
            {success}
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex flex-wrap items-end gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
            </div>
          </div>
          
          {/* Quick date selection buttons */}
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={setToday}
              className="px-3 py-1 bg-pink-100 text-pink-700 rounded-md hover:bg-pink-200 text-sm"
            >
              Today
            </button>
            <button
              onClick={setThisWeek}
              className="px-3 py-1 bg-pink-100 text-pink-700 rounded-md hover:bg-pink-200 text-sm"
            >
              This Week
            </button>
            <button
              onClick={setThisMonth}
              className="px-3 py-1 bg-pink-100 text-pink-700 rounded-md hover:bg-pink-200 text-sm"
            >
              This Month
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {['overview', 'transactions', 'expenses', 'invoices', 'reports'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${
                    activeTab === tab
                      ? 'border-pink-500 text-pink-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* ✅ REDESIGNED: Primary Financial Metrics - Beautiful Accounting Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Total Revenue Card */}
              <div className="bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl shadow-lg p-6 text-white overflow-hidden relative">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-3">
                    <div className="p-3 bg-white bg-opacity-20 rounded-lg backdrop-blur-sm flex-shrink-0">
                      <FaMoneyBillWave className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex items-center text-white text-opacity-90">
                      <FaArrowUp className="mr-1" />
                      <span className="text-sm font-medium">Revenue</span>
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-white text-opacity-90 mb-1">Total Revenue</h3>
                  <p className="text-2xl font-bold truncate">{formatCurrency(financialData.totalRevenue)}</p>
                  <p className="text-xs text-white text-opacity-75 mt-2 truncate">
                    {dateRange.startDate} to {dateRange.endDate}
                  </p>
                </div>
              </div>

              {/* Total Expenses Card */}
              <div className="bg-gradient-to-br from-rose-500 to-pink-600 rounded-xl shadow-lg p-6 text-white overflow-hidden relative">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-3">
                    <div className="p-3 bg-white bg-opacity-20 rounded-lg backdrop-blur-sm flex-shrink-0">
                      <FaArrowDown className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex items-center text-white text-opacity-90">
                      <FaReceipt className="mr-1" />
                      <span className="text-sm font-medium">Expenses</span>
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-white text-opacity-90 mb-1">Total Expenses</h3>
                  <p className="text-2xl font-bold truncate">{formatCurrency(financialData.totalExpenses)}</p>
                  <p className="text-xs text-white text-opacity-75 mt-2">Operating costs</p>
                </div>
              </div>

              {/* Net Profit Card */}
              <div className={`bg-gradient-to-br ${financialData.netProfit >= 0 ? 'from-blue-500 to-indigo-600' : 'from-orange-500 to-red-600'} rounded-xl shadow-lg p-6 text-white overflow-hidden relative`}>
                <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-3">
                    <div className="p-3 bg-white bg-opacity-20 rounded-lg backdrop-blur-sm flex-shrink-0">
                      <FaBalanceScale className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex items-center text-white text-opacity-90">
                      <FaChartLine className="mr-1" />
                      <span className="text-sm font-medium">Profit</span>
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-white text-opacity-90 mb-1">Net Profit</h3>
                  <p className="text-2xl font-bold truncate">{formatCurrency(financialData.netProfit)}</p>
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-xs text-white text-opacity-75">Margin:</p>
                    <div className="flex items-center">
                      <FaPercentage className="mr-1 text-xs" />
                      <span className="text-sm font-bold">{calculateProfitMargin()}%</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Inventory Cost Card */}
              <div className="bg-gradient-to-br from-purple-500 to-violet-600 rounded-xl shadow-lg p-6 text-white overflow-hidden relative">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-3">
                    <div className="p-3 bg-white bg-opacity-20 rounded-lg backdrop-blur-sm flex-shrink-0">
                      <FaWallet className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex items-center text-white text-opacity-90">
                      <FaFileInvoiceDollar className="mr-1" />
                      <span className="text-sm font-medium">Assets</span>
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-white text-opacity-90 mb-1">Inventory Value</h3>
                  <p className="text-2xl font-bold truncate">{formatCurrency(financialData.inventoryCost)}</p>
                  <p className="text-xs text-white text-opacity-75 mt-2">Total stock value</p>
                </div>
              </div>
            </div>

            {/* Secondary Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Financial Health Card */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
                  <FaChartLine className="mr-2 text-pink-600" />
                  Financial Health
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-700 font-medium">Profit Margin</span>
                    <span className={`font-bold text-lg ${getFinancialHealthStatus().color}`}>
                      {calculateProfitMargin()}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 rounded-lg" style={{backgroundColor: getFinancialHealthStatus().bg}}>
                    <span className="text-slate-700 font-medium">Status</span>
                    <span className={`font-bold text-lg ${getFinancialHealthStatus().color}`}>
                      {getFinancialHealthStatus().status}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-700 font-medium">VAT Payable</span>
                    <span className="font-bold text-lg text-orange-600 truncate ml-2">
                      {formatCurrency(financialData.vatPayable)}
                    </span>
                  </div>
                  <div className="mt-4 p-4 bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Gross Profit:</span>
                      <span className="font-semibold text-slate-900 truncate ml-2">{formatCurrency(financialData.grossProfit)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Operating Exp:</span>
                      <span className="font-semibold text-slate-900 truncate ml-2">{formatCurrency(financialData.operatingExpenses)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">COGS:</span>
                      <span className="font-semibold text-slate-900 truncate ml-2">{formatCurrency(financialData.cogs)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Revenue by Sub-Business Card */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
                  <FaMoneyBillWave className="mr-2 text-green-600" />
                  Revenue by Sub-Business
                </h3>
                <div className="space-y-3">
                  {Object.entries(financialData.revenueBySubBusiness).map(([subBusiness, revenue]) => (
                    <div key={subBusiness} className="flex justify-between items-center p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition">
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full mr-3 ${
                          subBusiness === 'Salon' ? 'bg-pink-500' :
                          subBusiness === 'Spa' ? 'bg-blue-500' : 'bg-orange-500'
                        }`}></div>
                        <span className="text-slate-700 font-medium">{subBusiness}</span>
                      </div>
                      <span className="font-bold text-green-600 truncate ml-4">
                        {formatCurrency(revenue)}
                      </span>
                    </div>
                  ))}
                  <div className="pt-3 border-t border-slate-200">
                    <div className="flex justify-between items-center font-bold p-3 bg-green-50 rounded-lg">
                      <span className="text-slate-900">Total</span>
                      <span className="text-green-600 truncate ml-4">
                        {formatCurrency(Object.values(financialData.revenueBySubBusiness).reduce((a, b) => a + b, 0))}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'transactions' && (
          <div className="bg-white rounded-lg shadow-md">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Sales Transactions</h3>
                <button
                  onClick={() => exportReport('transactions')}
                  className="px-4 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700"
                >
                  Export PDF
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Items
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sub-Business
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {transactions.length > 0 ? (
                    expandTransactions(transactions).map((transaction) => (
                      <tr key={transaction._id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(transaction.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {transaction.customer?.name || 'Walk-in Customer'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {transaction.items?.length || 0} items
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className="capitalize">
                            {transaction.paymentMethod?.replace('_', ' ') || 'Cash'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {transaction.displaySubBusiness || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                          {formatCurrency(transaction.totalAmount || transaction.total)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                        No transactions found for selected period
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'expenses' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Record New Expense</h3>
              <form onSubmit={handleExpenseSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
                  <input
                    type="number"
                    value={expenseForm.amount}
                    onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <input
                    type="text"
                    value={expenseForm.description}
                    onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                  <input
                    type="date"
                    value={expenseForm.date}
                    onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sub-Business</label>
                  <select
                    value={expenseForm.subBusiness}
                    onChange={(e) => setExpenseForm({ ...expenseForm, subBusiness: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 w-full"
                    required
                  >
                    <option value="Salon">Salon</option>
                    <option value="Spa">Spa</option>
                    <option value="Gym">Gym</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <button
                    type="submit"
                    className="px-4 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700"
                    disabled={loading}
                  >
                    {loading ? 'Recording...' : 'Record Expense'}
                  </button>
                </div>
              </form>
            </div>

            <div className="bg-white rounded-lg shadow-md">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900">Expense Records</h3>
                  <button
                    onClick={() => exportReport('expenses')}
                    className="px-4 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700"
                  >
                    Export PDF
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Sub-Business
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {expenses.length > 0 ? (
                      expenses.map((expense) => (
                        <tr key={expense._id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {new Date(expense.date).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">{expense.description}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {expense.subBusiness}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">
                            {formatCurrency(expense.amount)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                          No expenses found for selected period
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'invoices' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Invoice</h3>
              <form onSubmit={handleInvoiceSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Customer Name</label>
                  <input
                    type="text"
                    value={invoiceForm.customerName}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, customerName: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Customer Details</label>
                  <input
                    type="text"
                    value={invoiceForm.customerDetails}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, customerDetails: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                  <input
                    type="date"
                    value={invoiceForm.date}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, date: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sub-Business</label>
                  <select
                    value={invoiceForm.subBusiness}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, subBusiness: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 w-full"
                    required
                  >
                    <option value="Salon">Salon</option>
                    <option value="Spa">Spa</option>
                    <option value="Gym">Gym</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Items</h4>
                  {invoiceForm.items.map((item, index) => (
                    <div key={index} className="grid grid-cols-3 gap-4 mb-2">
                      <input
                        type="text"
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => updateInvoiceItem(index, 'description', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                        required
                      />
                      <input
                        type="number"
                        placeholder="Unit Price"
                        value={item.unitPrice}
                        onChange={(e) => updateInvoiceItem(index, 'unitPrice', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                        required
                      />
                      <input
                        type="number"
                        placeholder="Quantity"
                        value={item.quantity}
                        onChange={(e) => updateInvoiceItem(index, 'quantity', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                        required
                      />
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addInvoiceItem}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                  >
                    Add Item
                  </button>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Subtotal</label>
                  <input
                    type="number"
                    value={invoiceForm.subtotal}
                    readOnly
                    className="px-3 py-2 border border-gray-300 rounded-md bg-gray-100 w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">VAT (7.5%)</label>
                  <input
                    type="number"
                    value={invoiceForm.vat}
                    readOnly
                    className="px-3 py-2 border border-gray-300 rounded-md bg-gray-100 w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Total Amount</label>
                  <input
                    type="number"
                    value={invoiceForm.totalAmount}
                    readOnly
                    className="px-3 py-2 border border-gray-300 rounded-md bg-gray-100 w-full"
                  />
                </div>
                <div className="md:col-span-2">
                  <button
                    type="submit"
                    className="px-4 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700"
                    disabled={loading}
                  >
                    {loading ? 'Creating...' : 'Create Invoice'}
                  </button>
                </div>
              </form>
            </div>

            <div className="bg-white rounded-lg shadow-md">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900">Invoice Management</h3>
                  <button
                    onClick={() => exportReport('invoices')}
                    className="px-4 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700"
                  >
                    Export PDF
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Invoice #
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Sub-Business
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {invoices.length > 0 ? (
                      invoices.map((invoice) => (
                        <tr key={invoice._id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            #{invoice._id.slice(-6)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {invoice.customerName || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {new Date(invoice.date).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {invoice.subBusiness}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {formatCurrency(invoice.totalAmount)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                          No invoices found for selected period
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'reports' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Reports</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <button
                  onClick={() => alert('Coming Soon: Profit & Loss Statement report will be available in the next update!')}
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-left"
                >
                  <h4 className="font-medium text-gray-900">Profit & Loss Statement</h4>
                  <p className="text-sm text-gray-600 mt-1">Comprehensive income statement</p>
                  <span className="inline-block mt-2 px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded-full">Coming Soon</span>
                </button>
                <button
                  onClick={() => alert('Coming Soon: Balance Sheet report will be available in the next update!')}
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-left"
                >
                  <h4 className="font-medium text-gray-900">Balance Sheet</h4>
                  <p className="text-sm text-gray-600 mt-1">Assets, liabilities & equity</p>
                  <span className="inline-block mt-2 px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded-full">Coming Soon</span>
                </button>
                <button
                  onClick={() => alert('Coming Soon: Cash Flow Statement report will be available in the next update!')}
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-left"
                >
                  <h4 className="font-medium text-gray-900">Cash Flow Statement</h4>
                  <p className="text-sm text-gray-600 mt-1">Operating, investing & financing</p>
                  <span className="inline-block mt-2 px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded-full">Coming Soon</span>
                </button>
                <button
                  onClick={() => setShowVatModal(true)}
                  className="p-4 border border-purple-200 rounded-lg hover:bg-purple-50 text-left transition-colors"
                >
                  <h4 className="font-medium text-gray-900">VAT Collection Report</h4>
                  <p className="text-sm text-gray-600 mt-1">Monthly VAT collected, daily breakdown & transactions</p>
                  <span className="inline-block mt-2 px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">Available</span>
                </button>
                <button
                  onClick={() => alert('Coming Soon: Expense Analysis report will be available in the next update!')}
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-left"
                >
                  <h4 className="font-medium text-gray-900">Expense Analysis</h4>
                  <p className="text-sm text-gray-600 mt-1">Detailed expense breakdown</p>
                  <span className="inline-block mt-2 px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded-full">Coming Soon</span>
                </button>
                <button
                  onClick={() => alert('Coming Soon: Revenue Analysis report will be available in the next update!')}
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-left"
                >
                  <h4 className="font-medium text-gray-900">Revenue Analysis</h4>
                  <p className="text-sm text-gray-600 mt-1">Revenue trends and analysis</p>
                  <span className="inline-block mt-2 px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded-full">Coming Soon</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>

    {/* VAT Report Modal */}
    {showVatModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <FaPercentage className="text-purple-700 text-lg" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">VAT Collection Report</h2>
                <p className="text-xs text-gray-500">Export monthly VAT data</p>
              </div>
            </div>
            <button
              onClick={() => setShowVatModal(false)}
              className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-gray-700 transition-colors"
              disabled={vatGenerating}
            >
              ✕
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-6">
            {/* Month selector */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Select Month</label>
              <select
                value={vatMonth}
                onChange={(e) => setVatMonth(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-800"
                disabled={vatGenerating}
              >
                {vatMonthOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Format selector */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Export Format</label>
              <div className="flex gap-3">
                <button
                  onClick={() => setVatFormat('xlsx')}
                  disabled={vatGenerating}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg border-2 font-medium text-sm transition-all ${
                    vatFormat === 'xlsx'
                      ? 'border-purple-600 bg-purple-50 text-purple-700'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <FaFileInvoiceDollar />
                  Excel (.xlsx)
                </button>
                <button
                  onClick={() => setVatFormat('pdf')}
                  disabled={vatGenerating}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg border-2 font-medium text-sm transition-all ${
                    vatFormat === 'pdf'
                      ? 'border-pink-600 bg-pink-50 text-pink-700'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <FaReceipt />
                  PDF
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                {vatFormat === 'xlsx'
                  ? 'Coloured & formatted spreadsheet — best for accountants'
                  : 'Print-ready document — best for filing & submission'}
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-3 p-6 pt-0">
            <button
              onClick={() => setShowVatModal(false)}
              disabled={vatGenerating}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={generateVatReport}
              disabled={vatGenerating}
              className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm text-white transition-colors flex items-center justify-center gap-2 ${
                vatGenerating
                  ? 'bg-purple-400 cursor-not-allowed'
                  : 'bg-purple-600 hover:bg-purple-700'
              }`}
            >
              {vatGenerating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FaChartLine />
                  Generate Report
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default Accounting;